/**
 * Career Intelligence Content — display-only helpers.
 *
 * Source of truth: public.career_intelligence_content (Registry v1.1.0,
 * content layer only). NEVER used for scoring, matching, or engine
 * computation. NEVER written from the client.
 *
 * Resolution supports:
 *   - Canonical CI ids like `CI-001`
 *   - Legacy engine aliases like `WK0112` (stored in legacy_aliases jsonb)
 */
import { supabase } from "@/integrations/supabase/client";

export type CareerSectionValue = unknown;

export type CareerContentSummary = {
  ci_id: string;
  canonical_name: string;
  career_family: string | null;
  career_cluster: string | null;
  level: string | null;
  isco08_major_group: string | null;
  isco08_group_name: string | null;
  isco08_sub_major_group: string | null;
  isco08_minor_group: string | null;
  isco08_unit_group: string | null;
  isco08_skill_level: number | null;
  isco08_match_method: string | null;
  isco08_match_confidence: number | null;
  alternative_titles: string[];
  legacy_aliases: Array<{ alias: string; alias_type?: string }>;
};

export type CareerContentFull = CareerContentSummary & {
  esco_occupation_uri: string | null;
  record_class: string | null;
  status: string | null;
  schema_version: string | null;
  registry_version: string | null;
  sections: Record<string, Record<string, unknown>>;
};

const LIST_COLUMNS =
  "ci_id, canonical_name, career_family, career_cluster, level, isco08_major_group, isco08_group_name, isco08_sub_major_group, isco08_minor_group, isco08_unit_group, isco08_skill_level, isco08_match_method, isco08_match_confidence, alternative_titles, legacy_aliases";

const FULL_COLUMNS =
  LIST_COLUMNS +
  ", esco_occupation_uri, record_class, status, schema_version, registry_version, sections";

function normalizeAliases(raw: unknown): Array<{ alias: string; alias_type?: string }> {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((a) => (typeof a === "string" ? { alias: a } : (a as any)))
    .filter((a) => a && typeof a.alias === "string");
}

function normalizeAlt(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((s): s is string => typeof s === "string");
}

function toSummary(row: any): CareerContentSummary {
  return {
    ci_id: row.ci_id,
    canonical_name: row.canonical_name,
    career_family: row.career_family,
    career_cluster: row.career_cluster,
    level: row.level,
    isco08_major_group: row.isco08_major_group,
    isco08_group_name: row.isco08_group_name,
    isco08_sub_major_group: row.isco08_sub_major_group ?? null,
    isco08_minor_group: row.isco08_minor_group ?? null,
    isco08_unit_group: row.isco08_unit_group ?? null,
    isco08_skill_level: row.isco08_skill_level ?? null,
    isco08_match_method: row.isco08_match_method ?? null,
    isco08_match_confidence:
      row.isco08_match_confidence == null ? null : Number(row.isco08_match_confidence),
    alternative_titles: normalizeAlt(row.alternative_titles),
    legacy_aliases: normalizeAliases(row.legacy_aliases),
  };
}

/**
 * Resolve a career reference (CI id OR legacy WK alias) to the full content
 * record. Display-only. Returns null when not found.
 */
export async function resolveCareerContent(idOrAlias: string): Promise<CareerContentFull | null> {
  const key = (idOrAlias || "").trim();
  if (!key) return null;
  const client = supabase as any;

  // 1) direct ci_id
  {
    const { data } = await client
      .from("career_intelligence_content")
      .select(FULL_COLUMNS)
      .eq("ci_id", key)
      .maybeSingle();
    if (data) return { ...toSummary(data), ...data, sections: data.sections ?? {} } as CareerContentFull;
  }

  // 2) legacy alias lookup via jsonb contains
  const { data } = await client
    .from("career_intelligence_content")
    .select(FULL_COLUMNS)
    .contains("legacy_aliases", [{ alias: key }])
    .maybeSingle();
  if (data) return { ...toSummary(data), ...data, sections: data.sections ?? {} } as CareerContentFull;

  return null;
}

/** Resolve many career refs (mixed ci_id/WK alias) in one round-trip. */
export async function resolveManyCareerSummaries(
  refs: string[]
): Promise<Record<string, CareerContentSummary>> {
  const uniq = Array.from(new Set(refs.map((r) => (r || "").trim()).filter(Boolean)));
  if (!uniq.length) return {};
  const client = supabase as any;
  const out: Record<string, CareerContentSummary> = {};

  const ciIds = uniq.filter((r) => r.toUpperCase().startsWith("CI-"));
  const aliasIds = uniq.filter((r) => !r.toUpperCase().startsWith("CI-"));

  if (ciIds.length) {
    const { data } = await client
      .from("career_intelligence_content")
      .select(LIST_COLUMNS)
      .in("ci_id", ciIds);
    for (const row of (data ?? []) as any[]) {
      const s = toSummary(row);
      out[s.ci_id] = s;
    }
  }

  if (aliasIds.length) {
    // one OR'd query using contains; safe fallback = per-alias
    for (const alias of aliasIds) {
      const { data } = await client
        .from("career_intelligence_content")
        .select(LIST_COLUMNS)
        .contains("legacy_aliases", [{ alias }])
        .maybeSingle();
      if (data) {
        const s = toSummary(data);
        out[alias] = s;
        // also expose under ci_id for consumers
        out[s.ci_id] = s;
      }
    }
  }

  return out;
}

export type CareerSortKey =
  | "demand_desc"
  | "name_asc"
  | "name_desc"
  | "family_asc"
  | "cluster_asc"
  | "level_asc"
  | "level_desc"
  | "isco_asc";

export const CAREER_SORT_OPTIONS: Array<{ value: CareerSortKey; label: string }> = [
  { value: "demand_desc", label: "Most in demand" },
  { value: "name_asc", label: "Name · A to Z" },
  { value: "name_desc", label: "Name · Z to A" },
  { value: "family_asc", label: "Career family" },
  { value: "cluster_asc", label: "Cluster" },
  { value: "level_asc", label: "Level · entry first" },
  { value: "level_desc", label: "Level · senior first" },
  { value: "isco_asc", label: "ISCO-08 group" },
];

export function isCareerSortKey(v: string | null | undefined): v is CareerSortKey {
  return !!v && CAREER_SORT_OPTIONS.some((o) => o.value === v);
}

type OrderSpec = { column: string; ascending: boolean };

const SORT_ORDERS: Record<CareerSortKey, OrderSpec[]> = {
  // Handled in `browseByDemand`, not in SQL. Listed so the record stays total.
  demand_desc: [{ column: "canonical_name", ascending: true }],
  name_asc: [{ column: "canonical_name", ascending: true }],
  name_desc: [{ column: "canonical_name", ascending: false }],
  family_asc: [
    { column: "career_family", ascending: true },
    { column: "canonical_name", ascending: true },
  ],
  cluster_asc: [
    { column: "career_cluster", ascending: true },
    { column: "canonical_name", ascending: true },
  ],
  level_asc: [
    { column: "level", ascending: true },
    { column: "canonical_name", ascending: true },
  ],
  level_desc: [
    { column: "level", ascending: false },
    { column: "canonical_name", ascending: true },
  ],
  isco_asc: [
    { column: "isco08_major_group", ascending: true },
    { column: "canonical_name", ascending: true },
  ],
};

export type BrowseFilters = {
  q?: string;
  family?: string;
  cluster?: string;
  level?: string;
  sort?: CareerSortKey;
  limit?: number;
  offset?: number;
};

export type BrowsePage = {
  rows: CareerContentSummary[];
  total: number;
};

/** The filter clauses, shared by the SQL-ordered and demand-ordered paths. */
function applyFilters(q: any, f: BrowseFilters) {
  if (f.family) q = q.eq("career_family", f.family);
  if (f.cluster) q = q.eq("career_cluster", f.cluster);
  if (f.level) q = q.eq("level", f.level);

  if (f.q && f.q.trim()) {
    const term = f.q.trim();
    // canonical name partial match OR alternative_titles contains
    q = q.or(
      `canonical_name.ilike.%${term.replace(/[,%()]/g, " ")}%,alternative_titles.cs.["${term.replace(/"/g, '\\"')}"]`
    );
  }
  return q;
}

export async function browseCareerContent(f: BrowseFilters): Promise<BrowsePage> {
  const limit = Math.min(Math.max(f.limit ?? 30, 1), 100);
  const offset = Math.max(f.offset ?? 0, 0);
  const client = supabase as any;

  if ((f.sort ?? "demand_desc") === "demand_desc") {
    return browseByDemand(f, limit, offset);
  }

  let q = applyFilters(
    client.from("career_intelligence_content").select(LIST_COLUMNS, { count: "exact" }),
    f,
  );

  // deterministic ordering: chosen sort, then ci_id as a stable tie-break
  for (const o of SORT_ORDERS[f.sort ?? "demand_desc"]) {
    q = q.order(o.column, { ascending: o.ascending, nullsFirst: false });
  }
  q = q.order("ci_id", { ascending: true }).range(offset, offset + limit - 1);

  const { data, count } = await q;
  return {
    rows: ((data ?? []) as any[]).map(toSummary),
    total: count ?? 0,
  };
}

/**
 * "Most in demand" cannot be an ORDER BY: the demand band lives in the market
 * table and the sealed workbook, not on this content row. So we read the
 * filtered id list once (two columns, bulk-paged), order it in memory against
 * the demand index, and then fetch only the page the learner is looking at.
 *
 * The id list is cached per filter set, so paging further down the list does
 * not re-read it.
 */
const ORDER_PAGE = 1000;
const orderCache = new Map<string, { ids: string[]; total: number }>();

function orderKey(f: BrowseFilters): string {
  return JSON.stringify([f.q ?? "", f.family ?? "", f.cluster ?? "", f.level ?? ""]);
}

async function browseByDemand(
  f: BrowseFilters, limit: number, offset: number,
): Promise<BrowsePage> {
  const client = supabase as any;
  const key = orderKey(f);
  let ordered = orderCache.get(key);

  if (!ordered) {
    const rows: Array<{ ciId: string; name: string }> = [];
    for (let start = 0; ; start += ORDER_PAGE) {
      const q = applyFilters(
        client.from("career_intelligence_content").select("ci_id, canonical_name"),
        f,
      )
        .order("ci_id", { ascending: true })
        .range(start, start + ORDER_PAGE - 1);
      const { data } = await q;
      const batch = (data ?? []) as any[];
      for (const r of batch) rows.push({ ciId: r.ci_id, name: r.canonical_name ?? "" });
      if (batch.length < ORDER_PAGE) break;
    }

    const { loadDemandIndex, compareByDemand } = await import("@/lib/careerDemandRank");
    const index = await loadDemandIndex();
    rows.sort((a, b) => compareByDemand(a, b, index));
    ordered = { ids: rows.map((r) => r.ciId), total: rows.length };
    orderCache.set(key, ordered);
  }

  const slice = ordered.ids.slice(offset, offset + limit);
  if (slice.length === 0) return { rows: [], total: ordered.total };

  const { data } = await client
    .from("career_intelligence_content")
    .select(LIST_COLUMNS)
    .in("ci_id", slice);

  const byId = new Map<string, CareerContentSummary>();
  for (const row of ((data ?? []) as any[]).map(toSummary)) byId.set(row.ci_id, row);

  return {
    rows: slice.map((id) => byId.get(id)).filter((r): r is CareerContentSummary => !!r),
    total: ordered.total,
  };
}



export async function loadFacets(): Promise<{
  families: string[];
  clusters: string[];
  levels: string[];
}> {
  const client = supabase as any;
  const { data } = await client
    .from("career_intelligence_content")
    .select("career_family, career_cluster, level");
  const rows = (data ?? []) as any[];
  const uniq = (k: string) =>
    Array.from(new Set(rows.map((r) => r[k]).filter((v) => typeof v === "string" && v))).sort();
  return {
    families: uniq("career_family"),
    clusters: uniq("career_cluster"),
    levels: uniq("level"),
  };
}

/** Human-readable section titles (12 canonical sections). */
export const SECTION_LABELS: Record<string, string> = {
  student_fit: "Student Fit",
  career_story: "Career Story",
  human_energy: "Human Energy",
  daily_reality: "Daily Reality",
  future_skills: "Future Skills",
  human_purpose: "Human Purpose",
  career_journey: "Career Journey",
  explainability: "Explainability",
  experience_engine: "Experience Engine",
  human_environment: "Human Environment",
  ai_future_intelligence: "AI & Future Intelligence",
  labour_market_intelligence: "Labour Market Intelligence",
};

export const SECTION_ORDER: string[] = [
  "career_story",
  "student_fit",
  "daily_reality",
  "human_purpose",
  "human_energy",
  "human_environment",
  "career_journey",
  "future_skills",
  "ai_future_intelligence",
  "labour_market_intelligence",
  "experience_engine",
  "explainability",
];

/** Turn a snake_case key into a human label. */
export function humanizeKey(k: string): string {
  return k
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}


/**
 * The ISCO-08 coding for one career, plus the ILO group rows behind it.
 *
 * Display only — the codes come from the governed resolvers, never from the
 * browser. Returns null when the career is not in the content layer.
 */
export type OccupationCoding = {
  ci_id: string;
  canonical_name: string;
  isco08_major_group: string | null;
  isco08_sub_major_group: string | null;
  isco08_minor_group: string | null;
  isco08_unit_group: string | null;
  isco08_skill_level: number | null;
  isco08_match_method: string | null;
  isco08_match_confidence: number | null;
  /** The ILO hierarchy, broadest first, for whichever codes we hold. */
  hierarchy: Array<{ code: string; level: number; title: string }>;
  /** Definition and example job titles for the most precise group we hold. */
  definition: string | null;
  examples: string | null;
};

const CODING_COLUMNS =
  "ci_id, canonical_name, isco08_major_group, isco08_sub_major_group, isco08_minor_group, isco08_unit_group, isco08_skill_level, isco08_match_method, isco08_match_confidence";

export async function fetchOccupationCoding(ciId: string): Promise<OccupationCoding | null> {
  const key = (ciId || "").trim();
  if (!key) return null;
  const client = supabase as any;

  const { data: row } = await client
    .from("career_intelligence_content")
    .select(CODING_COLUMNS)
    .eq("ci_id", key)
    .maybeSingle();
  if (!row) return null;

  const codes = [
    row.isco08_major_group,
    row.isco08_sub_major_group,
    row.isco08_minor_group,
    row.isco08_unit_group,
  ].filter((c): c is string => typeof c === "string" && c.length > 0);

  let hierarchy: OccupationCoding["hierarchy"] = [];
  let definition: string | null = null;
  let examples: string | null = null;

  if (codes.length) {
    const { data: groups } = await client
      .from("isco08_groups")
      .select("code, level, title, definition, included_occupations")
      .in("code", codes)
      .order("level", { ascending: true });
    const rows = (groups ?? []) as Array<Record<string, any>>;
    hierarchy = rows.map((g) => ({ code: g.code, level: Number(g.level), title: g.title }));
    const deepest = rows[rows.length - 1];
    definition = deepest?.definition ?? null;
    examples = deepest?.included_occupations ?? null;
  }

  return {
    ci_id: row.ci_id,
    canonical_name: row.canonical_name,
    isco08_major_group: row.isco08_major_group ?? null,
    isco08_sub_major_group: row.isco08_sub_major_group ?? null,
    isco08_minor_group: row.isco08_minor_group ?? null,
    isco08_unit_group: row.isco08_unit_group ?? null,
    isco08_skill_level: row.isco08_skill_level ?? null,
    isco08_match_method: row.isco08_match_method ?? null,
    isco08_match_confidence:
      row.isco08_match_confidence == null ? null : Number(row.isco08_match_confidence),
    hierarchy,
    definition,
    examples,
  };
}
